import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fetchBuyerHome, fetchGigs } from "@/lib/api";
import GigCard from "@/components/GigCard";
import { ArrowRight, Package, Heart, TrendingUp, Star } from "lucide-react";
import { fileUrl } from "@/lib/api";

export default function BuyerHomePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState([]);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (user) {
      fetchBuyerHome().then(setData).catch(() => setData({}));
      fetchGigs({ limit: 8, featured: true, sort: "rating" }).then(setFeatured).catch(() => setFeatured([]));
    }
  }, [user]);

  if (!user || !data) return <div className="p-16 text-center">Loading…</div>;

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      {/* Hero greeting */}
      <div className="border-b border-black/10 pb-8 mb-10">
        <div className="overline text-primary mb-2">◆ WELCOME BACK</div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
          Hey {user.name?.split(" ")[0]} — what are you shipping today?
        </h1>
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/browse" data-testid="buyer-home-browse" className="bg-primary text-primary-foreground px-5 py-3 mono text-xs hover:bg-primary/90 flex items-center gap-2">
            EXPLORE GIGS <ArrowRight className="w-4 h-4"/>
          </Link>
          <Link to="/dashboard" data-testid="buyer-home-orders" className="border border-foreground px-5 py-3 mono text-xs hover:bg-foreground hover:text-background flex items-center gap-2">
            <Package className="w-4 h-4"/> MY ORDERS
          </Link>
          <Link to="/favorites" data-testid="buyer-home-favs" className="border border-foreground/30 px-5 py-3 mono text-xs hover:border-foreground flex items-center gap-2">
            <Heart className="w-4 h-4"/> SAVED
          </Link>
        </div>
      </div>

      {/* Active orders */}
      {data.recent_orders?.length > 0 && (
        <Section title="◆ YOUR RECENT ORDERS" subtitle="Track and manage what's in flight">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.recent_orders.map((o) => (
              <Link
                key={o.order_id}
                to={`/order/${o.order_id}`}
                data-testid={`buyer-home-order-${o.order_id}`}
                className="border border-foreground/15 bg-card p-4 hover:border-foreground transition"
              >
                {o.gig_cover && <img src={fileUrl(o.gig_cover)} className="aspect-video w-full object-cover mb-3 border border-foreground/10" alt=""/>}
                <div className="font-medium line-clamp-2 mb-2 text-sm">{o.gig_title}</div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{o.seller_name}</span>
                  <span className="mono uppercase px-2 py-1 border border-foreground/30">{o.status.replace("_"," ")}</span>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Editor's Picks - featured=true only */}
      {featured.length > 0 && (
        <section data-testid="buyer-editors-picks-section" className="mb-12">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="overline text-primary mb-1 flex items-center gap-2">
                <Star className="w-4 h-4 fill-primary"/>◆ EDITOR'S PICKS
              </div>
              <h2 className="text-2xl font-bold tracking-tight">Handpicked by the Noxi team</h2>
            </div>
            <Link to="/browse?featured=1" data-testid="buyer-editors-picks-view-all" className="mono text-xs hover:text-primary whitespace-nowrap">VIEW ALL →</Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-2 px-2 snap-x snap-mandatory">
            {featured.map((g, i) => (
              <div key={g.gig_id} data-testid={`buyer-editors-pick-${i}`} className="min-w-[260px] sm:min-w-[280px] max-w-[320px] snap-start">
                <GigCard gig={g} testId={`buyer-editors-pick-card-${i}`}/>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recommended */}
      {data.recommended?.length > 0 && (
        <Section title="◆ RECOMMENDED FOR YOU" subtitle="Based on your recent activity">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.recommended.slice(0, 4).map((g, i) => <GigCard key={g.gig_id} gig={g} testId={`buyer-rec-${i}`}/>)}
          </div>
        </Section>
      )}

      {/* Saved */}
      {data.saved?.length > 0 && (
        <Section title="◆ FROM YOUR FAVORITES" subtitle="Ready when you are">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.saved.map((g, i) => <GigCard key={g.gig_id} gig={g} testId={`buyer-saved-${i}`}/>)}
          </div>
        </Section>
      )}

      {/* Trending */}
      <Section title="◆ TRENDING NOW" subtitle="The most ordered gigs on Noxi" icon={<TrendingUp className="w-4 h-4"/>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(data.trending || []).slice(0, 4).map((g, i) => <GigCard key={g.gig_id} gig={g} testId={`buyer-trend-${i}`}/>)}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, children, icon }) {
  return (
    <section className="mb-12">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="overline text-primary mb-1 flex items-center gap-2">{icon}{title}</div>
          <h2 className="text-2xl font-bold tracking-tight">{subtitle}</h2>
        </div>
      </div>
      {children}
    </section>
  );
}
